import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { RecipeIngredientsList } from "./RecipeIngredientsList";
import { RecipeFunFacts } from "./RecipeFunFacts";
import { RecipeSteps } from "./RecipeSteps";
import { RecipeJournal } from "./RecipeJournal";
import { RecipeOverview } from "./RecipeOverview";
import { HealthyAlternative } from "@/types/dietary";
import { useIsMobile } from "@/hooks/use-mobile";
import { generateHealthyAlternatives } from "@/utils/recipe/healthyAlternatives";
import { ScienceCards } from "./recipe/ScienceCards";
import { generateScienceCards } from "@/utils/recipe/scienceExplanations";

interface RecipeTransformationProps {
  recipeData: {
    title: string;
    imageUrl?: string;
    ingredients: string[];
    steps: Array<{
      instruction: string;
      science?: string;
    }>;
    prepTime?: string;
    cookTime?: string;
    temperature?: string;
  };
}

export const RecipeTransformation = ({ recipeData }: RecipeTransformationProps) => {
  const [currentAlternatives, setCurrentAlternatives] = useState<HealthyAlternative[]>([]);
  const [generatedAlternatives, setGeneratedAlternatives] = useState<HealthyAlternative[]>([]);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (recipeData.ingredients.length > 0) {
      const alternatives = generateHealthyAlternatives(recipeData.ingredients);
      setGeneratedAlternatives(alternatives);
    }
  }, [recipeData.ingredients]);

  const processedSteps = recipeData.steps.map(step => {
    const cleanInstruction = step.instruction
      .trim()
      .replace(/\n+/g, ' ')
      .replace(/\s+/g, ' ')
      .replace(/^\d+\.\s*/, '');

    let cleanScience = step.science;
    
    if (cleanScience) {
      const hasTortillas = recipeData.ingredients.some(ingredient => 
        ingredient.toLowerCase().includes('tortilla'));
      
      if (!hasTortillas && cleanScience.toLowerCase().includes('tortilla')) {
        cleanScience = null;
      }

      const isDessert = recipeData.ingredients.some(ingredient => 
        ['sugar', 'chocolate', 'vanilla', 'cookie', 'cake'].some(term => 
          ingredient.toLowerCase().includes(term)
        )
      );

      const isMainDish = recipeData.ingredients.some(ingredient => 
        ['chicken', 'beef', 'pork', 'fish', 'tofu'].some(term => 
          ingredient.toLowerCase().includes(term)
        )
      );
      
      const hasSugar = recipeData.ingredients.some(ingredient => 
        ingredient.toLowerCase().includes('sugar'));
      
      const hasButter = recipeData.ingredients.some(ingredient => 
        ingredient.toLowerCase().includes('butter'));
      
      if (!hasSugar && cleanScience.toLowerCase().includes('sugar')) {
        cleanScience = null;
      }
      
      if (!hasButter && cleanScience.toLowerCase().includes('butter')) {
        cleanScience = null;
      }

      if (isMainDish && (cleanScience.toLowerCase().includes('cake') || cleanScience.toLowerCase().includes('batter'))) {
        cleanScience = null;
      }

      if (isDessert && (cleanScience.toLowerCase().includes('sauté') || cleanScience.toLowerCase().includes('simmer'))) {
        cleanScience = null;
      }
      
      if (!step.instruction.toLowerCase().includes('fold') && cleanScience.toLowerCase().includes('folding')) {
        cleanScience = null;
      }
      
      const isBaking = recipeData.temperature || 
                       step.instruction.toLowerCase().includes('bake') || 
                       step.instruction.toLowerCase().includes('oven');
                       
      if (!isBaking && cleanScience.toLowerCase().includes('oven door')) {
        cleanScience = null;
      }
    }

    return {
      instruction: cleanInstruction,
      science: cleanScience
    };
  }).filter(step => step.instruction.length > 0);

  const scienceCards = generateScienceCards(recipeData.ingredients, recipeData.steps);

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  return (
    <motion.div 
      className="grid gap-6 sm:gap-8 mt-6 sm:mt-10 px-4 sm:px-0"
      variants={containerVariants}
      initial="hidden"
      animate="show"
    >
      {recipeData.imageUrl && (
        <motion.div 
          className="relative w-full overflow-hidden rounded-xl shadow-lg"
          variants={itemVariants}
        >
          <div className="aspect-video relative">
            <img 
              src={recipeData.imageUrl} 
              alt={recipeData.title}
              className="w-full h-full object-cover"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.onerror = null;
                target.src = '/placeholder.svg';
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6">
              <h2 className="text-xl sm:text-2xl font-bold text-white">{recipeData.title}</h2>
            </div>
          </div>
        </motion.div>
      )}

      <motion.div className="w-full" variants={itemVariants}>
        <RecipeOverview 
          prepTime={recipeData.prepTime}
          cookTime={recipeData.cookTime}
          temperature={recipeData.temperature}
        />
      </motion.div>
      
      <motion.div variants={itemVariants}>
        <ScienceCards cards={scienceCards} />
      </motion.div>
      
      <motion.div variants={itemVariants}>
        <RecipeFunFacts 
          ingredients={recipeData.ingredients}
          healthyAlternatives={currentAlternatives}
          onDietaryChange={setCurrentAlternatives}
        />
      </motion.div>
      
      <motion.div variants={itemVariants}>
        <RecipeIngredientsList 
          ingredients={recipeData.ingredients}
          healthyAlternatives={currentAlternatives}
        />
      </motion.div>

      <motion.div variants={itemVariants}>
        <RecipeSteps steps={processedSteps} />
      </motion.div>

      <motion.div className="w-full" variants={itemVariants}>
        <RecipeJournal />
      </motion.div>
    </motion.div>
  );
};
